import { useCallback, useMemo, useRef, useState } from 'react';
import { ExpandedState } from '@tanstack/react-table';

type RowWithId = {
  id: string;
};

type UseSearchExpandedStateOptions<T extends RowWithId> = {
  searchValue: string;
  data: T[];
};

type UseSearchExpandedStateResult = {
  expanded: ExpandedState;
  onExpandedChange: (
    updaterOrValue: ExpandedState | ((old: ExpandedState) => ExpandedState)
  ) => void;
};

/**
 * Custom hook for managing TanStack Table expansion state with search-driven auto-expand.
 *
 * When a search value is present, all rows are automatically expanded to show matches,
 * but users can still manually collapse/expand rows during the search.
 * When search is cleared, manual expansion state from before the search is restored.
 *
 * @param options.searchValue - The current search/filter value
 * @param options.data - The table data (top-level rows that can be expanded)
 * @returns expanded state and onExpandedChange handler for useReactTable config
 *
 * @example
 * ```tsx
 * const { expanded, onExpandedChange } = useSearchExpandedState({
 *   searchValue: filterText,
 *   data: tableData,
 * });
 *
 * const table = useReactTable({
 *   // ... other config
 *   state: { expanded },
 *   onExpandedChange,
 * });
 * ```
 */
export function useSearchExpandedState<T extends RowWithId>({
  searchValue,
  data,
}: UseSearchExpandedStateOptions<T>): UseSearchExpandedStateResult {
  // Track user-controlled expansion state (used when not searching)
  const [manualExpanded, setManualExpanded] = useState<ExpandedState>({});

  // Track user overrides during search (rows explicitly collapsed/expanded while searching)
  const [searchOverrides, setSearchOverrides] = useState<ExpandedState>({});

  // Track previous search value to detect when it changes
  const prevSearchValueRef = useRef(searchValue);

  // Determine if we should auto-expand due to search
  const isSearching = searchValue.trim().length > 0;

  // Clear search overrides when search value changes
  // This ensures groups re-expand when the user types a new search term
  if (prevSearchValueRef.current !== searchValue) {
    prevSearchValueRef.current = searchValue;
    if (Object.keys(searchOverrides).length > 0) {
      setSearchOverrides({});
    }
  }

  // Compute the expanded state
  const expanded = useMemo((): ExpandedState => {
    if (!isSearching) {
      return manualExpanded;
    }

    // When searching: start with all rows expanded, then apply user overrides
    const allExpanded = data.reduce<Record<string, boolean>>((acc, row) => {
      acc[row.id] = true;
      return acc;
    }, {});

    // Apply any user overrides (collapsed rows during search)
    if (searchOverrides === true) {
      return allExpanded;
    }
    return { ...allExpanded, ...searchOverrides };
  }, [isSearching, data, manualExpanded, searchOverrides]);

  // Handle expansion changes
  const onExpandedChange = useCallback(
    (updaterOrValue: ExpandedState | ((old: ExpandedState) => ExpandedState)) => {
      if (isSearching) {
        // During search, track overrides separately
        // TanStack Table passes an updater function that expects the FULL expanded state,
        // so we need to compute it from allExpanded + current overrides, then extract overrides
        setSearchOverrides(prevOverrides => {
          // Compute current full expanded state (all rows expanded + overrides)
          const allExpanded = data.reduce<Record<string, boolean>>((acc, row) => {
            acc[row.id] = true;
            return acc;
          }, {});
          const currentExpanded =
            prevOverrides === true ? allExpanded : { ...allExpanded, ...prevOverrides };

          // Apply the updater to get the new desired state
          const newExpanded =
            typeof updaterOrValue === 'function' ? updaterOrValue(currentExpanded) : updaterOrValue;

          // Extract only the overrides (differences from allExpanded)
          if (newExpanded === true) {
            return {};
          }

          const overrides: Record<string, boolean> = {};

          // Check for rows that were removed (collapsed) - TanStack removes keys to collapse
          for (const id of Object.keys(allExpanded)) {
            if (!(id in newExpanded)) {
              // Row was removed from expanded state = collapsed
              overrides[id] = false;
            }
          }

          // Check for rows explicitly set to false
          for (const [id, isExpanded] of Object.entries(newExpanded)) {
            if (isExpanded === false) {
              overrides[id] = false;
            }
          }

          return overrides;
        });
      } else {
        // When not searching, update the manual state
        setManualExpanded(updaterOrValue);
      }
    },
    [isSearching, data]
  );

  return { expanded, onExpandedChange };
}
